import React from "react";
import { AdminApis } from "../../apis/adminApi";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { ToastContainer, toast } from "react-toastify";
import { NavLink } from "react-router-dom";
import configs from "../../configs";
import CardPageVisits from "./CardPageVisits";
import CardRenewSubscription from "./CardRenewSubscription";
import { PhoneInput } from "react-contact-number-input";
import EmojiPicker from "emoji-picker-react";
import { FaWhatsapp } from "react-icons/fa";
import { AiOutlineWarning } from "react-icons/ai";
import {
  FiCopy as FiCopyIcon, FiEdit2 as FiEdit2Icon, FiTrash2, FiExternalLink,
  FiLink, FiMousePointer, FiCheckCircle, FiAlertTriangle, FiPlus, FiSearch,
  FiX, FiSmile, FiRadio,
} from "react-icons/fi";

export default function CardMyLinks() {
  /* ── List state ────────────────────────────────────────────────── */
  const [effect, setEffect] = React.useState("");
  const [loader, setLoader] = React.useState(true);
  const [data, setData] = React.useState([]);
  const [searchTerm, setSearchTerm] = React.useState("");

  /* ── Edit modal ────────────────────────────────────────────────── */
  const [editVisible, setEditVisible] = React.useState(false);
  const [selectedLink, setSelectedLink] = React.useState(null);
  const [editMessage, setEditMessage] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  /* ── Delete modal ──────────────────────────────────────────────── */
  const [deleteVisible, setDeleteVisible] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);

  /* ── Create modal ──────────────────────────────────────────────── */
  const [createVisible, setCreateVisible] = React.useState(false);
  const [createName, setCreateName] = React.useState("");
  const [createPhone, setCreatePhone] = React.useState("");
  const [createMessage, setCreateMessage] = React.useState("");
  const [createType, setCreateType] = React.useState("message");
  const [nameExist, setNameExist] = React.useState("");
  const [showEmoji, setShowEmoji] = React.useState(false);
  const [creating, setCreating] = React.useState(false);

  /* ── Body scroll lock ──────────────────────────────────────────── */
  const anyModal = editVisible || deleteVisible || createVisible;
  React.useEffect(() => {
    document.body.style.overflow = anyModal ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [anyModal]);

  /* ── Fetch links ───────────────────────────────────────────────── */
  React.useEffect(() => {
    setLoader(true);
    AdminApis.getAllLinks()
      .then((res) => { if (res?.data) setData(res.data); })
      .finally(() => setLoader(false));
  }, [effect]);

  /* ── Name availability check ───────────────────────────────────── */
  React.useEffect(() => {
    if (!createName) return;
    const clean = createName.toLowerCase().replace(/\s+/g, "-").replace(/[^a-zA-Z0-9-]/g, "");
    AdminApis.searchName({ name: clean }).then((res) => {
      if (res?.data) setNameExist(res.data.data);
    });
  }, [createName]);

  /* ── Helpers ───────────────────────────────────────────────────── */
  const cleanName = (n) => n.toLowerCase().replace(/\s+/g, "-").replace(/[^a-zA-Z0-9-]/g, "");

  const openEdit = (link) => { setSelectedLink(link); setEditMessage(link?.link_info?.message || ""); setEditVisible(true); };
  const openDelete = (link) => { setSelectedLink(link); setDeleteVisible(true); };
  const openCreate = () => {
    setCreateName(""); setCreatePhone(""); setCreateMessage("");
    setCreateType("message"); setNameExist(""); setShowEmoji(false);
    setCreateVisible(true);
  };

  const getClickCount = (link) =>
    link?.short_url?.visits?.filter((v) => v?.operating_system !== "0").length || 0;

  /* ── Edit submit ───────────────────────────────────────────────── */
  const handleEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await AdminApis.editLink({
        message: editMessage?.replace(/[\n\r]+/g, "."),
        id: selectedLink?.id,
        name: selectedLink?.name,
        phone_number: selectedLink?.link_info?.phone_number,
      });
      if (res?.data) {
        setEditVisible(false);
        setEffect("v_" + Date.now());
        toast.success(res.data.message || "Link updated!");
      }
    } catch { toast.error("Failed to update link."); }
    finally { setSaving(false); }
  };

  /* ── Delete submit ─────────────────────────────────────────────── */
  const handleDelete = async (e) => {
    e.preventDefault();
    setDeleting(true);
    try {
      const res = await AdminApis.deleteLink(selectedLink?.id);
      if (res?.data) {
        setDeleteVisible(false);
        setEffect("d_" + Date.now());
        toast.success("Link deleted.");
      }
    } catch { toast.error("Failed to delete link."); }
    finally { setDeleting(false); }
  };

  /* ── Create submit ─────────────────────────────────────────────── */
  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    const fd = new FormData();
    fd.append("phone_number", (createPhone?.countryCode + createPhone?.phoneNumber)?.replace(/ /g, "") || "");
    fd.append("name", cleanName(createName));
    fd.append("message", createMessage?.replace(/[\n\r]+/g, "."));
    try {
      const res = createType === "catalog"
        ? await AdminApis.createCatalog(fd)
        : await AdminApis.createLink(fd);
      if (res?.data) {
        setCreateVisible(false);
        setEffect("c_" + Date.now());
        toast.success("Link created successfully!");
      } else {
        toast.error(res?.response?.data?.message || "Link name already in use.");
      }
    } catch { toast.error("Failed to create link."); }
    finally { setCreating(false); }
  };

  /* ── Filtered list ─────────────────────────────────────────────── */
  const filteredLinks = React.useMemo(() => {
    const links = data?.link || [];
    if (!searchTerm.trim()) return links;
    return links.filter((l) =>
      Object.values(l).join(" ").toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [data, searchTerm]);

  /* ── Loading skeleton ──────────────────────────────────────────── */
  if (loader) {
    return (
      <div className="p-4">
        <div className="animate-pulse space-y-4">
          <div className="flex justify-between">
            <div className="h-8 bg-gray-100 rounded-xl w-40" />
            <div className="h-10 bg-gray-200 rounded-2xl w-36" />
          </div>
          <div className="h-10 bg-gray-100 rounded-xl w-full max-w-sm" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-3xl border border-gray-100 overflow-hidden shadow-sm">
                <div className="h-12 bg-gray-200" />
                <div className="p-4 space-y-3">
                  <div className="h-3 bg-gray-100 rounded-full w-3/4" />
                  <div className="h-16 bg-gray-100 rounded-xl" />
                  <div className="h-3 bg-gray-100 rounded-full w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (data?.data === "sub_expired") return <CardRenewSubscription />;

  /* ── Render ────────────────────────────────────────────────────── */
  return (
    <div className="min-h-screen bg-gray-50">

      {/* ── Page header ─────────────────────────────────────────────── */}
      <div className="bg-white border-b border-gray-200 px-3 sm:px-4 py-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#0071BC]/10 flex items-center justify-center">
              <FiLink className="text-[#0071BC]" size={16} />
            </div>
            <div>
              <h1 className="text-lg font-extrabold text-gray-900">My WhatsApp Links</h1>
              <p className="text-xs text-gray-400 mt-0.5">
                {(data?.link?.length || 0)} {(data?.link?.length || 0) === 1 ? "link" : "links"} created
              </p>
            </div>
          </div>

          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#0071BC] text-white text-sm font-bold rounded-2xl hover:bg-[#005fa0] active:scale-95 transition-all shadow-sm hover:shadow-md self-start sm:self-auto"
          >
            <FiPlus size={16} strokeWidth={2.5} />
            Create New Link
          </button>
        </div>

        {/* Info banner */}
        <div className="mt-3 flex items-start gap-2.5 bg-blue-50 border border-blue-100 rounded-2xl px-3 py-2.5">
          <FiLink className="text-[#0071BC] flex-shrink-0 mt-0.5" size={13} />
          <p className="text-xs text-gray-600 leading-relaxed">
            Create custom WhatsApp links with built-in click analytics. Share them anywhere — customers click once and land straight in your WhatsApp chat.
          </p>
        </div>
      </div>

      {/* ── Content ─────────────────────────────────────────────────── */}
      <div className="px-3 sm:px-4 py-5">

        {/* Search */}
        {(data?.link?.length || 0) > 0 && (
          <div className="relative max-w-sm mb-5">
            <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={13} />
            <input
              type="text"
              placeholder="Search links…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-8 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0071BC]/20 focus:border-[#0071BC] transition-all shadow-sm"
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <FiX size={12} />
              </button>
            )}
          </div>
        )}

        {/* Cards grid */}
        {filteredLinks.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredLinks.map((link) => {
              const clicks = getClickCount(link);
              const linkUrl = `${configs?.baseRedirect}/${link?.name}`;
              return (
                <div key={link.id} className="group bg-white rounded-3xl border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 overflow-hidden flex flex-col">

                  {/* Header */}
                  <div className="flex items-center justify-between px-4 py-3 bg-[#0071BC]">
                    <div className="flex items-center gap-2 min-w-0">
                      <FiLink className="text-white/70 flex-shrink-0" size={12} />
                      <span className="text-white font-bold text-sm truncate">{link?.name}</span>
                    </div>
                    <CopyToClipboard text={linkUrl} onCopy={() => toast.success("Copied!")}>
                      <button className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1 bg-white/20 hover:bg-white/30 text-white text-[11px] font-semibold rounded-full border border-white/30 transition-all active:scale-95">
                        <FiCopyIcon size={10} /> Copy
                      </button>
                    </CopyToClipboard>
                  </div>

                  {/* Body */}
                  <NavLink to={`/link-details/${link?.id}`} className="flex-1 flex flex-col p-4 gap-3 hover:bg-gray-50/50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5 text-gray-500 text-xs font-medium">
                        <FaWhatsapp className="text-green-500" size={12} />
                        {link?.link_info?.phone_number?.replace(/ /g, "") || "—"}
                      </div>
                      <div className={`flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full ${clicks > 0 ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-400"}`}>
                        <FiMousePointer size={9} />
                        {clicks} click{clicks !== 1 ? "s" : ""}
                      </div>
                    </div>

                    <div className="bg-gray-50 border border-gray-100 rounded-2xl px-3 py-2.5 min-h-[60px]">
                      <p className="text-xs text-gray-500 leading-relaxed line-clamp-3">
                        {link?.link_info?.message || <span className="italic text-gray-300">No message set</span>}
                      </p>
                    </div>

                    <div className="flex items-center gap-1.5 text-[11px] text-[#0071BC] font-medium truncate">
                      <FiExternalLink size={10} />
                      <span className="truncate">{linkUrl}</span>
                    </div>
                  </NavLink>

                  {/* Footer */}
                  <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                    <div className="flex items-center gap-2">
                      <span className="flex items-center gap-1 text-[11px] font-bold text-green-700 bg-green-50 border border-green-100 px-2.5 py-1 rounded-full">
                        <FiCheckCircle size={9} /> Active
                      </span>
                      {link?.type === "catalog" && (
                        <span className="text-[11px] font-semibold text-blue-700 bg-blue-50 border border-blue-100 px-2.5 py-1 rounded-full">Catalog</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {link?.type !== "catalog" && (
                        <button onClick={() => openEdit(link)} className="w-8 h-8 flex items-center justify-center rounded-xl text-gray-400 hover:text-[#0071BC] hover:bg-blue-50 transition-all" title="Edit">
                          <FiEdit2Icon size={13} />
                        </button>
                      )}
                      <button onClick={() => openDelete(link)} className="w-8 h-8 flex items-center justify-center rounded-xl text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all" title="Delete">
                        <FiTrash2 size={13} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (data?.link?.length || 0) === 0 ? (
          <CardPageVisits />
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-3xl bg-gray-100 flex items-center justify-center mb-4">
              <FiSearch className="text-gray-300" size={26} />
            </div>
            <h3 className="text-base font-extrabold text-gray-900 mb-1">No links found</h3>
            <p className="text-gray-400 text-sm mb-4">Try a different search term.</p>
            <button onClick={() => setSearchTerm("")} className="px-5 py-2 text-sm font-bold text-gray-600 bg-white border border-gray-200 rounded-full hover:bg-gray-50 shadow-sm transition-colors">
              Clear Search
            </button>
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════
          CREATE MODAL
      ══════════════════════════════════════════════════════════════ */}
      {createVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setCreateVisible(false)} />
          <div className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl flex flex-col max-h-[90vh]">

            {/* Header */}
            <div className="flex items-center justify-between px-5 pt-4 pb-4 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-[#0071BC]/10 flex items-center justify-center">
                  <FaWhatsapp className="text-[#0071BC]" size={16} />
                </div>
                <div>
                  <h2 className="text-base font-extrabold text-gray-900">Create WhatsApp Link</h2>
                  <p className="text-xs text-gray-400 mt-0.5">Shareable link that opens your WhatsApp</p>
                </div>
              </div>
              <button onClick={() => setCreateVisible(false)} className="w-8 h-8 flex items-center justify-center rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all">
                <FiX size={15} />
              </button>
            </div>

            {/* Scrollable form */}
            <form onSubmit={handleCreate} className="flex flex-col flex-1 overflow-hidden">
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">

                {/* Brand name */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">
                    Link Name <span className="text-gray-400 font-normal">(Keep it short)</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      required
                      placeholder="Eg. shoes-with-mark"
                      value={createName}
                      onChange={(e) => setCreateName(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0071BC]/20 focus:border-[#0071BC] transition-all pr-10"
                    />
                    <FiSearch className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#0071BC]" size={14} />
                  </div>

                  {/* Availability badge */}
                  {createName && (
                    <div className="mt-2 flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${nameExist > 0 ? "bg-red-50 text-red-600 border border-red-100" : "bg-green-50 text-green-700 border border-green-100"}`}>
                        {nameExist > 0 ? <FiX size={10} /> : <FiCheckCircle size={10} />}
                        {nameExist > 0 ? "Name taken" : "Available"}
                      </span>
                      <span className="text-xs text-gray-400">link.mygupta.co/<strong className="text-gray-600">{cleanName(createName)}</strong></span>
                    </div>
                  )}
                  {!createName && (
                    <p className="text-xs text-gray-400 mt-1.5">link.mygupta.co/<span className="text-gray-500">your-name</span></p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">Your WhatsApp Number</label>
                  <PhoneInput countryCode="ng" onChange={setCreatePhone} placeholder="Enter phone number" />
                </div>

                {/* Link type */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-2">Link Type</label>
                  <div className="flex gap-3">
                    {["message", "catalog"].map((t) => (
                      <label key={t} className={`flex-1 flex items-center gap-2.5 px-4 py-3 rounded-2xl border-2 cursor-pointer transition-all ${createType === t ? "border-[#0071BC] bg-[#0071BC]/5" : "border-gray-200 bg-gray-50 hover:border-gray-300"}`}>
                        <input type="radio" name="linkType" value={t} checked={createType === t} onChange={() => setCreateType(t)} className="sr-only" />
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${createType === t ? "border-[#0071BC]" : "border-gray-300"}`}>
                          {createType === t && <div className="w-2 h-2 rounded-full bg-[#0071BC]" />}
                        </div>
                        <span className={`text-sm font-semibold capitalize ${createType === t ? "text-[#0071BC]" : "text-gray-500"}`}>{t}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Catalog warning */}
                {createType === "catalog" && (
                  <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3">
                    <AiOutlineWarning className="text-amber-500 flex-shrink-0 mt-0.5" size={16} />
                    <p className="text-xs text-amber-700 leading-relaxed">
                      Before creating this link, confirm you have an active WhatsApp Business catalog.
                    </p>
                  </div>
                )}

                {/* Message */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-bold text-gray-700">Pre-filled Message</label>
                    <button
                      type="button"
                      onClick={() => setShowEmoji((v) => !v)}
                      className="flex items-center gap-1 text-xs text-gray-500 hover:text-[#0071BC] transition-colors"
                    >
                      😊 <span>{showEmoji ? "Hide" : "Add"} emoji</span>
                    </button>
                  </div>

                  {showEmoji && (
                    <div className="mb-3">
                      <EmojiPicker
                        onEmojiClick={(e) => { setCreateMessage((m) => m + e.emoji); setShowEmoji(false); }}
                        width="100%"
                        height={300}
                      />
                    </div>
                  )}

                  <textarea
                    rows={3}
                    disabled={createType !== "message"}
                    value={createMessage}
                    onChange={(e) => setCreateMessage(e.target.value)}
                    placeholder="Add a pre-filled message that users will send to you. Eg. Hello, I want to buy snickers."
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#0071BC]/20 focus:border-[#0071BC] transition-all disabled:opacity-40 disabled:cursor-not-allowed leading-relaxed"
                  />
                  <p className="text-[11px] text-gray-400 mt-1.5">This message will be pre-filled when someone taps your link. They can still edit it before sending.</p>
                </div>
              </div>

              {/* Sticky footer */}
              <div className="px-5 pb-5 pt-3 border-t border-gray-100 flex gap-2.5 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setCreateVisible(false)}
                  className="flex-1 py-3 text-sm font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-2xl transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating || nameExist === 1}
                  className="flex-1 py-3 text-sm font-bold text-white bg-[#0071BC] hover:bg-[#005fa0] rounded-2xl transition-all shadow-md hover:shadow-lg active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {creating ? "Creating…" : "Create Link"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          EDIT MODAL
      ══════════════════════════════════════════════════════════════ */}
      {editVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setEditVisible(false)} />
          <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between px-5 pt-4 pb-4 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-[#0071BC]/10 flex items-center justify-center">
                  <FiEdit2Icon className="text-[#0071BC]" size={14} />
                </div>
                <div>
                  <h2 className="text-base font-extrabold text-gray-900">Edit Message</h2>
                  <p className="text-xs text-gray-400 mt-0.5">Link: <span className="font-semibold text-[#0071BC]">{selectedLink?.name}</span></p>
                </div>
              </div>
              <button onClick={() => setEditVisible(false)} className="w-8 h-8 flex items-center justify-center rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all">
                <FiX size={15} />
              </button>
            </div>
            <form onSubmit={handleEdit} className="flex flex-col flex-1 overflow-hidden">
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-1.5">Pre-filled WhatsApp Message</label>
                  <textarea
                    rows={5}
                    value={editMessage}
                    onChange={(e) => setEditMessage(e.target.value)}
                    placeholder="Message customers will see when they tap your link…"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#0071BC]/20 focus:border-[#0071BC] transition-all leading-relaxed"
                  />
                </div>
                <div className="flex items-center gap-3 bg-gray-50 border border-gray-100 rounded-2xl px-4 py-3">
                  <FaWhatsapp className="text-green-500 flex-shrink-0" size={15} />
                  <div>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">Connected Number</p>
                    <p className="text-sm font-semibold text-gray-700 mt-0.5">{selectedLink?.link_info?.phone_number || "—"}</p>
                  </div>
                </div>
              </div>
              <div className="px-5 pb-5 pt-3 border-t border-gray-100 flex gap-2.5 flex-shrink-0">
                <button type="button" onClick={() => setEditVisible(false)} className="flex-1 py-3 text-sm font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-2xl transition-all">Cancel</button>
                <button type="submit" disabled={saving} className="flex-1 py-3 text-sm font-bold text-white bg-[#0071BC] hover:bg-[#005fa0] rounded-2xl transition-all shadow-md active:scale-[0.98] disabled:opacity-60">
                  {saving ? "Saving…" : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          DELETE MODAL
      ══════════════════════════════════════════════════════════════ */}
      {deleteVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setDeleteVisible(false)} />
          <div className="relative w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden">
            <div className="bg-red-50 border-b border-red-100 px-5 py-5 flex items-start gap-3">
              <div className="w-10 h-10 rounded-2xl bg-red-100 flex items-center justify-center flex-shrink-0">
                <FiAlertTriangle className="text-red-500" size={18} />
              </div>
              <div className="flex-1">
                <h2 className="text-base font-extrabold text-gray-900">Delete Link?</h2>
                <p className="text-sm text-gray-500 mt-0.5"><span className="font-semibold text-gray-700">{selectedLink?.name}</span> will be permanently removed.</p>
              </div>
              <button onClick={() => setDeleteVisible(false)} className="w-7 h-7 flex items-center justify-center rounded-xl text-gray-400 hover:text-gray-600 hover:bg-white/60 transition-all flex-shrink-0">
                <FiX size={13} />
              </button>
            </div>
            <div className="px-5 py-4">
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-3">What happens:</p>
              <ul className="space-y-2">
                {["The link immediately stops working", "All click analytics will be lost", "The link name becomes available to others", "Visitors will be redirected to gupta.link", "Buttons using this in Tiered Links will break"].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-gray-600">
                    <span className="w-4 h-4 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <FiX className="text-red-500" size={8} strokeWidth={3} />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="px-5 pb-5 flex gap-2.5">
              <button type="button" onClick={() => setDeleteVisible(false)} className="flex-1 py-3 text-sm font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-2xl transition-all">Keep Link</button>
              <form onSubmit={handleDelete} className="flex-1">
                <button type="submit" disabled={deleting} className="w-full py-3 text-sm font-bold text-white bg-red-500 hover:bg-red-600 rounded-2xl transition-all shadow-md active:scale-[0.98] disabled:opacity-60">
                  {deleting ? "Deleting…" : "Yes, Delete"}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="bottom-right" autoClose={2500} hideProgressBar theme="light" toastStyle={{ borderRadius: "12px" }} />
    </div>
  );
}
